import React, { useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import api from '../../services/api';

interface FlowRow {
  flow_id: string;
  flow: string;
  abriram: number;
  rascunhos: number;
  geraram: number;
  baixaram: number;
  falhas: number;
  tempo_mediano_s: number | null;
  nota_media: number | null;
  avaliacoes: number;
}

interface Dashboard {
  periodo_dias: number;
  totais: {
    geracoes: number;
    baixadas_pct: number | null;
    falhas_pct: number | null;
    geracoes_acima_de_2_min: number;
    cobrancas_de_geracao: number;
    cobrancas_sem_documento: number;
  };
  fluxos: FlowRow[];
}

// Metas de lançamento do plano (Fase 5)
const Kpi: React.FC<{ label: string; value: React.ReactNode; goal: string; ok?: boolean | null }> = ({ label, value, goal, ok }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 180, flex: 1 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} color={ok === false ? 'error.main' : ok ? 'success.main' : 'text.primary'}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">Meta: {goal}</Typography>
  </Paper>
);

const BetaDashboardPage: React.FC = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.get(`/admin/beta-dashboard?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setError('Não foi possível carregar o painel.'));
  }, [days]);

  const t = data?.totais;
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={700}>Painel do Beta</Typography>
          <Typography variant="body2" color="text.secondary">Uso e saúde da geração de documentos</Typography>
        </Box>
        <Select size="small" value={days} onChange={e => setDays(Number(e.target.value))}>
          <MenuItem value={7}>Últimos 7 dias</MenuItem>
          <MenuItem value={14}>Últimos 14 dias</MenuItem>
          <MenuItem value={30}>Últimos 30 dias</MenuItem>
        </Select>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress />}

      {data && t && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Kpi label="Peças geradas" value={t.geracoes} goal="—" />
            <Kpi label="Peças baixadas" value={t.baixadas_pct === null ? '—' : `${t.baixadas_pct}%`} goal="70% ou mais"
              ok={t.baixadas_pct === null ? null : t.baixadas_pct >= 70} />
            <Kpi label="Gerações com erro" value={t.falhas_pct === null ? '—' : `${t.falhas_pct}%`} goal="menos de 1%"
              ok={t.falhas_pct === null ? null : t.falhas_pct < 1} />
            <Kpi label="Cobrança sem documento" value={t.cobrancas_sem_documento} goal="zero" ok={t.cobrancas_sem_documento === 0} />
            <Kpi label="Gerações acima de 2 min" value={t.geracoes_acima_de_2_min} goal="—" />
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fluxo</TableCell>
                  <TableCell align="right">Abriram</TableCell>
                  <TableCell align="right">Rascunhos</TableCell>
                  <TableCell align="right">Geraram</TableCell>
                  <TableCell align="right">Baixaram</TableCell>
                  <TableCell align="right">Falhas</TableCell>
                  <TableCell align="right">Tempo mediano</TableCell>
                  <TableCell align="right">Nota média</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.fluxos.length === 0 && (
                  <TableRow><TableCell colSpan={8}>Sem uso no período.</TableCell></TableRow>
                )}
                {data.fluxos.map(r => (
                  <TableRow key={r.flow_id}>
                    <TableCell>{r.flow}</TableCell>
                    <TableCell align="right">{r.abriram}</TableCell>
                    <TableCell align="right">{r.rascunhos}</TableCell>
                    <TableCell align="right">{r.geraram}</TableCell>
                    <TableCell align="right">{r.baixaram}</TableCell>
                    <TableCell align="right" sx={{ color: r.falhas ? 'error.main' : undefined }}>{r.falhas}</TableCell>
                    <TableCell align="right">{r.tempo_mediano_s === null ? '—' : `${r.tempo_mediano_s} s`}</TableCell>
                    <TableCell align="right">{r.nota_media === null ? '—' : `${r.nota_media} (${r.avaliacoes})`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            "Abriram" e "Rascunhos" contam pessoas diferentes; "Geraram" e "Baixaram", documentos.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default BetaDashboardPage;
